<template>
  <div class="custom-card page-wrapper">
    <!-- Filtros de Búsqueda y Botones de Acción -->
    <v-card class="pa-4 mb-4 filter-card elevation-0">
      <v-row dense align="center">
        <!-- Pills / Active Filters on Left -->
        <v-col cols="12" lg="7" class="d-flex align-center">
          <div class="d-flex flex-wrap align-center gap-2 w-100">
            <!-- Filter Pills -->
            <div v-if="!filtrosActivos.length" class="text-caption grey--text text--darken-1 font-italic">
              Sin filtros activos. Mostrando todas las solicitudes.
            </div>
            <template v-else>
              <div v-for="filtro in filtrosActivos" :key="filtro" class="filter-pill">
                <v-icon small class="mr-1 primary--text">mdi-filter-variant</v-icon>
                <span class="pill-value">{{ filtro }}</span>
              </div>
            </template>
          </div>
        </v-col>

        <!-- Action buttons & Search on Right -->
        <v-col cols="12" lg="5" class="d-flex justify-lg-end justify-start flex-wrap align-center gap-2 mt-2 mt-lg-0">
          <!-- Ver Solicitud Button -->
          <v-btn
            depressed
            outlined
            color="primary"
            class="rounded-lg action-btn"
            title="Ver Solicitud"
            :disabled="solicitud == null"
            @click="verSolicitud"
          >
            <v-icon color="primary" class="mr-1">mdi-eye</v-icon>
            Ver
          </v-btn>

          <!-- Estado Select -->
          <div style="width: 200px;" class="mr-1">
            <v-select
              v-model="estado"
              :items="estadosSelect"
              label="Estado"
              outlined
              dense
              hide-details
              class="rounded-lg custom-input"
              @change="listarRestableciendoPagina">
              <template #selection="{ item }">
                <v-chip
                  v-if="item.value"
                  small
                  outlined
                  :color="coloresEstado[item.value] || 'grey'"
                  class="font-weight-bold">
                  {{ item.text }}
                </v-chip>
                <span v-else>{{ item.text }}</span>
              </template>
              <template #item="{ item }">
                <v-chip
                  v-if="item.value"
                  small
                  outlined
                  :color="coloresEstado[item.value] || 'grey'"
                  class="font-weight-bold">
                  {{ item.text }}
                </v-chip>
                <span v-else>{{ item.text }}</span>
              </template>
            </v-select>
          </div>

          <!-- Limpiar Filtros Button -->
          <v-btn
            depressed
            outlined
            color="grey darken-2"
            class="rounded-lg action-btn"
            title="Limpiar Filtros"
            :disabled="!hayFiltros"
            @click="limpiarFiltros"
          >
            <v-icon color="grey darken-2" class="mr-1">mdi-filter-off-outline</v-icon>
            Limpiar
          </v-btn>

          <!-- Recargar Button -->
          <v-btn
            depressed
            color="primary"
            class="rounded-lg action-btn"
            title="Recargar"
            :loading="cargandoSolicitudes"
            @click="listar"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Barra de Progreso -->
    <v-progress-linear v-if="cargandoSolicitudes" indeterminate color="primary" class="mb-4 rounded" height="4" />

    <!-- Listado Section -->
    <v-row dense>
      <v-col cols="12">
        <!-- Estado Vacío -->
        <div v-if="!cargandoSolicitudes && solicitudes.length === 0" class="empty-state-card py-12 px-6 text-center">
          <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-database-alert-outline</v-icon>
          <h3 class="text-h6 font-weight-medium grey--text text--darken-3 mb-1">Sin información para mostrar</h3>
          <p class="body-2 grey--text text--darken-1 max-w-md mx-auto mb-0">
            No se encontraron solicitudes de modificación de calificaciones para los filtros seleccionados.
          </p>
        </div>

        <!-- Listado con ag-grid -->
        <ListadoSolicitudesModificacionCalificacion
          v-else
          ref="listadoRef"
          :items="solicitudes"
          :cargando="cargandoSolicitudes"
          @selectItem="selectItem" />
      </v-col>
    </v-row>

    <!-- Paginación en parte inferior -->
    <div class="d-flex align-center justify-space-between flex-wrap mt-4 px-1 gap-2" v-if="solicitudes.length > 0">
      <div class="d-flex align-center">
        <span class="caption grey--text text--darken-1 font-weight-medium mr-4">
          Página {{ pagina }} de {{ metadatos?.totalPaginas || 1 }}
        </span>
        <v-select
          :items="[10, 25, 50, 75, 100, 500]"
          label="Items por página"
          v-model="itemsPorPagina"
          outlined
          dense
          hide-details
          style="max-width: 150px"
          @change="cambiarPagina"
          class="rounded-lg custom-input" />
      </div>

      <v-pagination
        v-model="pagina"
        :length="metadatos?.totalPaginas || 0"
        :total-visible="7"
        @input="listar"
        color="primary"
        class="custom-pagination" />
    </div>

    <!-- Diálogos Auxiliares -->
    <ConfirmacionAccion
      ref="confirmacionRef"
      :titulo="confirmacion.titulo"
      :realizandoAccion="confirmacion.realizandoAccion"
      @aceptar="confirmacion.fnAceptar">
      <template>
        <p v-html="confirmacion.mensaje"></p>
      </template>
    </ConfirmacionAccion>

    <DialogVerSolicitud
      ref="dialogVerSolicitudRef"
      @solicitud-revisada="manejarSolicitudRevisada" />

    <notifications group="notifications" position="top right" />
  </div>
</template>

<script>
import ListadoSolicitudesModificacionCalificacion from '../components/ListadoSolicitudesModificacionCalificacion.vue';
import SolicitudModificacionCalificacionService from '../../services/solicitud-modificacion-calificacion.service';
import ConfirmacionAccion from '@/views/pagos/components/ConfirmacionAccion.vue';
import DialogVerSolicitud from '../components/dialogs/DialogVerSolicitud.vue';
import {
  ESTADOS_SOLICITUD_MODIFICACION_CALIFICACION,
  ESTADOS_SOLICITUD_MODIFICACION_CALIFICACION_LIST,
  ESTADOS_SOLICITUD_MODIFICACION_CALIFICACION_COLORES,
} from '../../utils/constantes';
import { ACCIONES_A_CONFIRMAR } from '@/views/pagos/utils/constantes';

export default {
  name: 'SolicitudModificacionCalificacionLayout',
  components: {
    ListadoSolicitudesModificacionCalificacion,
    ConfirmacionAccion,
    DialogVerSolicitud,
  },
  data() {
    return {
      nombreItem: 'Solicitud Modificación de Calificación',
      solicitud: null,
      pagina: 1,
      itemsPorPagina: Number(localStorage.getItem(`${this.$route.name}-itemsPorPagina`)) || 10,
      metadatos: null,
      cargando: false,
      accionesAConfirmar: ACCIONES_A_CONFIRMAR,
      confirmacion: {
        titulo: 'Acción a confirmar',
        mensaje: '¿Está seguro de realizar esta acción?',
        realizandoAccion: false,
        fnAceptar: () => {
          window.alert("No se ha definido la función 'fnAceptar'");
        },
      },
      solicitudes: [],
      estado: null,
      estados: ESTADOS_SOLICITUD_MODIFICACION_CALIFICACION,
      estadosSelect: ESTADOS_SOLICITUD_MODIFICACION_CALIFICACION_LIST,
      coloresEstado: ESTADOS_SOLICITUD_MODIFICACION_CALIFICACION_COLORES,
      cargandoSolicitudes: false,
    };
  },
  created() {
    this.obtenerSolicitudes();
  },
  watch: {
    '$store.state.CARGO.sede.id'() {
      this.listarRestableciendoPagina();
    },
  },
  computed: {
    hayFiltros() {
      return this.estado !== null;
    },
    filtrosActivos() {
      const chips = [];

      if (this.estado !== null) {
        const estadoLabel = this.estadosSelect.find((e) => e.value === this.estado)?.text || this.estado;
        chips.push(`Estado: ${estadoLabel}`);
      }

      return chips;
    },
    puedeEnviar() {
      return (
        this.solicitud &&
        [
          this.estados.PENDIENTE,
          this.estados.GENERADO,
        ].includes(this.solicitud.estado)
      );
    },
    puedeSincronizar() {
      return this.solicitud && [this.estados.APROBADA].includes(this.solicitud.estado);
    },
  },
  methods: {
    selectItem(item) {
      this.solicitud = item;
    },
    updateSearchQuery(val) {
      this.$refs.listadoRef?.updateSearchQuery?.(val);
    },
    async obtenerDatosPaginadosConNotificacion(fnServicioListar, llave, llaveMetadatos, nombreRecurso, params = {}) {
      this.cargando = true;

      try {
        const { data } = await fnServicioListar({ params });
        const { datos, mensaje, metadatos } = data;

        this[llave] = datos;
        this[llaveMetadatos] = metadatos;
        this.$notify({
          group: 'notifications',
          title: nombreRecurso.toUpperCase(),
          text: mensaje || 'Listado exitoso',
          type: 'success',
        });
      } catch (error) {
        const mensaje = error?.response ? error.response?.data?.mensaje : error.message;

        this[llave] = [];
        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: mensaje || 'Error al listar',
          type: 'error',
        });
      } finally {
        this.cargando = false;
      }
    },
    listarRestableciendoPagina() {
      if (this.pagina !== 1) {
        this.pagina = 1;
      }

      this.listar();
    },
    cambiarPagina() {
      this.listarRestableciendoPagina();
      localStorage.setItem(`${this.$route.name}-itemsPorPagina`, this.itemsPorPagina);
    },
    limpiarFiltros() {
      this.estado = null;
      this.listarRestableciendoPagina();
    },
    listar() {
      const params = {
        pagina: this.pagina,
        itemsPorPagina: this.itemsPorPagina,
        estado: this.estado,
        sedeId: this.$store.state.CARGO.sede?.id,
      };

      this.obtenerSolicitudes(params);

      this.solicitud = null;
    },
    async getResponseAccion(accion) {
      if (accion === this.accionesAConfirmar.ELIMINAR) {
        return await SolicitudModificacionCalificacionService.remove(this.solicitud.id);
      }

      return null;
    },
    verSolicitud() {
      if (!this.solicitud) {
        return;
      }

      this.$refs.dialogVerSolicitudRef?.abrir(this.solicitud);
    },
    manejarSolicitudRevisada(data) {
      this.solicitudes=this.solicitudes.map(solicitud=>{
        if(solicitud.id===data.solicitudId){
          return {...solicitud,estadoSolicitud:data.estado, observacion:data.motivo};
        }
        return solicitud;
      })
    },
    confirmarEnvio() {
      if (!this.solicitud || !this.puedeEnviar) {
        return;
      }

      this.confirmacion.titulo = 'Enviar solicitud';
      this.confirmacion.mensaje = `¿Enviar la solicitud N° ${this.solicitud.id}?`;
      this.confirmacion.fnAceptar = this.enviar;
      this.$refs.confirmacionRef.abrir();
    },
    confirmarSincronizacion() {
      if (!this.solicitud || !this.puedeSincronizar) {
        return;
      }

      this.confirmacion.titulo = 'Sincronizar solicitud';
      this.confirmacion.mensaje = `¿Sincronizar la solicitud N° ${this.solicitud.id}?`;
      this.confirmacion.fnAceptar = this.sincronizar;
      this.$refs.confirmacionRef.abrir();
    },
    async enviar() {
      this.confirmacion.realizandoAccion = true;

      try {
        const response = await SolicitudModificacionCalificacionService.enviar(this.item.id);
        this.$refs.confirmacionRef.cerrar();
        this.listar();
        this.$notify({
          group: 'notifications',
          title: this.nombreItem.toUpperCase(),
          text: response?.data?.mensaje || 'Solicitud enviada correctamente',
          type: 'success',
        });
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje;
        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: mensaje || 'No se pudo enviar la solicitud',
          type: 'error',
        });
      } finally {
        this.confirmacion.realizandoAccion = false;
      }
    },
    async sincronizar() {
      this.confirmacion.realizandoAccion = true;

      try {
        const response = await SolicitudModificacionCalificacionService.sincronizar(this.solicitud.id);
        this.$refs.confirmacionRef.cerrar();
        this.listar();
        this.$notify({
          group: 'notifications',
          title: this.nombreItem.toUpperCase(),
          text: response?.data?.mensaje || 'Solicitud sincronizada correctamente',
          type: 'success',
        });
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje;
        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: mensaje || 'No se pudo sincronizar la solicitud',
          type: 'error',
        });
      } finally {
        this.confirmacion.realizandoAccion = false;
      }
    },

    async obtenerSolicitudes(params = {}) {
      this.cargandoSolicitudes = true;
      try {
        const { data } = await SolicitudModificacionCalificacionService.solicitudes(params);
        this.solicitudes = data?.datos || [];
        this.metadatos = data?.paginacion || null;
      } catch (error) {
        this.solicitudes = [];
        this.metadatos = null;
      } finally {
        this.cargandoSolicitudes = false;
      }
    },
  },
};
</script>

<style scoped>
.custom-card {
  border: 1px solid #dfdfdf;
  padding: 16px;
  margin: 0px 8px 0px 8px;
}

.page-wrapper {
  background-color: #fafbfc;
  border-radius: 12px;
}

.gap-3 {
  gap: 12px;
}

.gap-2 {
  gap: 8px;
}

/* Filter Card styling */
.filter-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05) !important;
}

.filter-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #334155;
  transition: all 0.2s ease;
}

.pill-value {
  font-weight: 600;
}

/* Action button styling */
.action-btn {
  height: 38px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  font-size: 0.8rem !important;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  transition: all 0.2s ease;
}

/* Custom input styling for search field */
.custom-input ::v-deep .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  background-color: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transition: all 0.2s ease;
}

.custom-input ::v-deep .v-input__control > .v-input__slot:hover {
  background-color: #f1f5f9 !important;
}

/* Pagination Custom Styles */
.custom-pagination {
  margin-top: 0 !important;
}

.custom-pagination ::v-deep .v-pagination__item {
  box-shadow: none !important;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.85rem;
  min-width: 32px;
  height: 32px;
}

.custom-pagination ::v-deep .v-pagination__item--active {
  background-color: #1565c0 !important;
  color: white !important;
  border-color: #1565c0 !important;
}

.custom-pagination ::v-deep .v-pagination__navigation {
  box-shadow: none !important;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  width: 32px;
  height: 32px;
}

/* Empty State Card */
.empty-state-card {
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  max-width: 600px;
  margin: 2rem auto;
}

.max-w-md {
  max-width: 28rem;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

::v-deep .v-select.v-text-field--outlined:not(.v-text-field--single-line).v-input--dense .v-select__selections {
  padding: 0px 0;
}

::v-deep .v-text-field .v-input__control .v-input__slot {
  min-height: auto !important;
  display: flex !important;
  align-items: center !important;
}

::v-deep .v-text-field--outlined.v-input--dense .v-label {
  top: 5px;
}

::v-deep .v-text-field .v-label {
  font-weight: 400;
}
</style>

